import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material'
import { GoogleAuthDialogProps } from './types'

/**
 * Google Calendar認証ダイアログ
 *
 * 認証が必要な場合に表示されるモーダルダイアログ。
 * - Google認証ボタン（Salesforce個人設定画面へのリンク）
 * - ヘルプドキュメントへのリンク
 */
export default function GoogleAuthDialog({ open, onGetAuthUrl, onClose, onAuthComplete }: GoogleAuthDialogProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [authWindowOpened, setAuthWindowOpened] = useState(false)

  /**
   * Google認証ボタンクリック
   * 新しいウィンドウでSalesforce個人設定画面を開く
   */
  const handleAuthenticate = async () => {
    setLoading(true)
    setError(null)

    try {
      const authUrl = await onGetAuthUrl()
      window.open(authUrl, '_blank', 'width=800,height=600')
      setAuthWindowOpened(true)
    } catch (err) {
      console.error('認証URL取得エラー:', err)
      setError('認証URLの取得に失敗しました。もう一度お試しください。')
    } finally {
      setLoading(false)
    }
  }

  /**
   * 認証完了ボタンクリック
   * 認証テストを実行して、成功したらダイアログを閉じる
   */
  const handleAuthComplete = async () => {
    setLoading(true)
    setError(null)

    try {
      await onAuthComplete()
      // 成功したらダイアログを閉じる
      onClose()
    } catch (err) {
      console.error('認証テストエラー:', err)
      setError('認証の確認に失敗しました。認証が完了していることを確認してください。')
    } finally {
      setLoading(false)
    }
  }

  /**
   * ダイアログを閉じる（キャンセル）
   */
  const handleClose = () => {
    if (!loading) {
      setAuthWindowOpened(false)
      setError(null)
      onClose()
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Google Calendar 連携設定</DialogTitle>
      <DialogContent>
        <Box sx={{ py: 2 }}>
          <Typography variant="body1" gutterBottom>
            面談記録の自動同期にはGoogle Calendarとの連携が必要です。
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            以下の手順で認証を行ってください。
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!authWindowOpened ? (
            <Box>
              <Typography variant="body2" gutterBottom>
                1. 「Google認証を開始」ボタンをクリック
              </Typography>
              <Typography variant="body2" gutterBottom>
                2. 開いたウィンドウでGoogle認証を完了
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                3. 認証完了後、「認証完了」ボタンをクリック
              </Typography>

              <Button
                variant="contained"
                color="primary"
                onClick={handleAuthenticate}
                disabled={loading}
                fullWidth
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Google認証を開始'}
              </Button>
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                認証ウィンドウを開きました。認証が完了したら「認証完了」ボタンをクリックしてください。
              </Alert>

              <Button
                variant="contained"
                color="success"
                onClick={handleAuthComplete}
                disabled={loading}
                fullWidth
                sx={{ mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : '認証完了'}
              </Button>

              <Button variant="outlined" onClick={handleAuthenticate} disabled={loading} fullWidth sx={{ mb: 2 }}>
                認証ウィンドウを再度開く
              </Button>
            </Box>
          )}

          <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              詳しい手順については、以下のヘルプドキュメントをご覧ください。
            </Typography>
            <Link
              href="https://www.notion.so/lifeistech/PR-2a8dc408133980cb88e6c702f75ab358"
              target="_blank"
              rel="noopener noreferrer"
            >
              <b>PR シートエディター 利用手順</b>
            </Link>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          キャンセル
        </Button>
      </DialogActions>
    </Dialog>
  )
}
